import Link from "next/link"
import { useRouter } from "next/router"

import {
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import {
  getDisplayedRunStatus,
  hasNonSuccessRunErrorStatus,
  StatusIcon,
} from "@/runs/status"
import { CopyableId, Tooltip, TruncatedText } from "@coval/ui"

import { RowEventBoundary } from "@/components/tables/RowEventBoundary"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"
import {
  formatFullTimestamp,
  formatRelativeTimestampWithTime,
} from "@/utils/formatting/format-timestamp"

import {
  formatMetricValue,
  formatMetricValueDetail,
  getMetricColumnColor,
} from "../utils/metricColumnUtils"
import { isMetricColumn } from "../utils/virtualRunsTableLayout"
import { RunTagsCell } from "./RunTagsCell"

export interface CellLookupProps {
  agentNames: Record<string, string>
  testSetNames: Record<string, string>
  metricDisplayNames: Record<string, string>
  metricRecordsById: Record<string, MetricRecordData | undefined>
  personasById: Record<string, { display_name?: string } | undefined>
  getModelType: (modelType: string) => string
  onAgentClick?: (agentId: string) => void
  onTestSetClick?: (testSetId: string) => void
  onPersonaClick?: (personaId: string) => void
  resolveCreator: (creator: string | null | undefined) => string
}

function CreatorCell({ display }: { display: string }) {
  return (
    <TruncatedText className="w-full text-coval-text-muted">
      {display}
    </TruncatedText>
  )
}

function CreatedCell({ createdAt }: { createdAt: string }) {
  return (
    <span
      className="block w-full truncate whitespace-nowrap text-sm"
      title={formatFullTimestamp(createdAt)}
    >
      {formatRelativeTimestampWithTime(createdAt)}
    </span>
  )
}

function NameCell({
  name,
  bold,
  badge,
}: {
  name: string
  bold?: boolean
  badge?: React.ReactNode
}) {
  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      {badge}
      <TruncatedText
        className={`min-w-0 flex-1${bold ? " font-semibold" : ""}`}
      >
        {name}
      </TruncatedText>
    </div>
  )
}

/**
 * Name cell for a resource a run references (agent, test set, persona).
 *
 * Renders a drawer-opening button when `onClick` is set, a link to the
 * resource's full page when only `path` is set, and plain text when neither
 * applies.
 *
 * The wrapper spans the cell while the button/link is capped at its available
 * width, so long names ellipsize without turning the rest of the cell into a
 * click target. The tooltip preserves the complete resource name.
 */
function ResourceNameCell({
  display,
  bold,
  ariaLabel,
  onClick,
  path,
}: {
  display: string
  bold?: boolean
  ariaLabel: string
  onClick?: () => void
  path?: string
}) {
  const router = useRouter()
  const queryParams = useQueryParams()
  const slugString =
    typeof router.query.slug === "string" ? router.query.slug : undefined
  const href =
    slugString && path ? queryParams.getHref(path, slugString) : undefined
  const textClassName = `block max-w-full truncate text-coval-info-text hover:underline${bold ? " font-semibold" : ""}`

  if (onClick) {
    return (
      <div className="w-full min-w-0">
        <Tooltip content={display}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onClick()
            }}
            className={`${textClassName} text-left`}
            aria-label={ariaLabel}
          >
            {display}
          </button>
        </Tooltip>
      </div>
    )
  }

  if (href) {
    return (
      <div className="w-full min-w-0">
        <Tooltip content={display}>
          <Link
            href={href}
            onClick={(event) => event.stopPropagation()}
            className={textClassName}
            aria-label={ariaLabel}
          >
            {display}
          </Link>
        </Tooltip>
      </div>
    )
  }

  return <NameCell name={display} bold={bold} />
}

function MetricValueBadge({
  aggregate = false,
  metricId,
  value,
  metricDisplayNames,
  metricRecordsById,
}: {
  aggregate?: boolean
  metricId: string
  value: number | undefined
  metricDisplayNames: Record<string, string>
  metricRecordsById: Record<string, MetricRecordData | undefined>
}) {
  if (!metricDisplayNames[metricId]) {
    return null
  }
  if (typeof value !== "number" || !isFinite(value)) {
    return null
  }
  const metricRecord = metricRecordsById[metricId]
  const color = getMetricColumnColor(value, metricRecord, aggregate)
  const formatted = formatMetricValue(value, metricRecord)
  const detail = formatMetricValueDetail(value, metricRecord)
  const badgeClassName = `${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses[color]}`
  if (detail === null) {
    return <span className={badgeClassName}>{formatted}</span>
  }
  return (
    <Tooltip content={detail}>
      <span className={badgeClassName}>{formatted}</span>
    </Tooltip>
  )
}

export function RunCellContent({
  columnKey,
  run,
  agentNames,
  testSetNames,
  metricDisplayNames,
  metricRecordsById,
  personasById,
  getModelType,
  onAgentClick,
  onTestSetClick,
  onPersonaClick,
  resolveCreator,
}: CellLookupProps & {
  columnKey: string
  run: RunsTableResult
}) {
  if (isMetricColumn(columnKey)) {
    const metricId = columnKey.slice("metric:".length)
    return (
      <MetricValueBadge
        metricId={metricId}
        value={run.metric_averages?.[metricId]}
        aggregate
        metricDisplayNames={metricDisplayNames}
        metricRecordsById={metricRecordsById}
      />
    )
  }

  switch (columnKey) {
    case "status":
      return (
        <StatusIcon
          status={getDisplayedRunStatus(run.status, run.error_status)}
          isErrorStatus={hasNonSuccessRunErrorStatus(run.error_status)}
          size={20}
        />
      )
    case "id":
      return <CopyableId id={run.id} label="Copy Run ID" />
    case "runName":
      return (
        <NameCell
          name={
            run.display_name?.trim() ||
            `Run on ${new Date(run.created_at).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}`
          }
        />
      )
    case "agentName": {
      const agentId = run.agent_id
      const agentDisplay = agentId ? agentNames[agentId] || agentId : "-"
      return (
        <ResourceNameCell
          display={agentDisplay}
          bold
          ariaLabel={`Open agent ${agentDisplay}`}
          onClick={
            agentId && onAgentClick ? () => onAgentClick(agentId) : undefined
          }
        />
      )
    }
    case "testSetName": {
      const testSetId = run.test_set_id
      const display = testSetId ? testSetNames[testSetId] || testSetId : "-"
      return (
        <ResourceNameCell
          display={display}
          ariaLabel={`Open test set ${display}`}
          onClick={
            testSetId && onTestSetClick
              ? () => onTestSetClick(testSetId)
              : undefined
          }
          path={testSetId ? `/test-sets/${testSetId}` : undefined}
        />
      )
    }
    case "personaId": {
      const personaId = run.persona_id_id
      const display = personaId
        ? personasById[personaId]?.display_name || personaId
        : "-"
      return (
        <ResourceNameCell
          display={display}
          ariaLabel={`Open persona ${display}`}
          onClick={
            personaId && onPersonaClick
              ? () => onPersonaClick(personaId)
              : undefined
          }
          path={personaId ? `/personas/${personaId}` : undefined}
        />
      )
    }
    case "modelType":
      return (
        <TruncatedText className="w-full">
          {getModelType(run.model_type)}
        </TruncatedText>
      )
    case "numSimulations":
      return <>{run.num_simulations || "-"}</>
    case "tags":
      return (
        <RowEventBoundary>
          <RunTagsCell runId={run.id} initialTags={run.tags ?? undefined} />
        </RowEventBoundary>
      )
    case "createdBy":
      return (
        <CreatorCell
          display={resolveCreator(run.created_by_user_id ?? run.created_by)}
        />
      )
    case "created":
      return <CreatedCell createdAt={run.created_at} />
    default:
      return null
  }
}

export function SimulationCellContent({
  columnKey,
  sim,
  agentNames,
  testSetNames,
  metricDisplayNames,
  metricRecordsById,
  personasById,
  getModelType,
  onAgentClick,
  onTestSetClick,
  onPersonaClick,
  resolveCreator,
}: CellLookupProps & {
  columnKey: string
  sim: SimulationsTableResult
}) {
  if (isMetricColumn(columnKey)) {
    const metricId = columnKey.slice("metric:".length)
    return (
      <MetricValueBadge
        metricId={metricId}
        value={sim.metric_values?.[metricId]}
        metricDisplayNames={metricDisplayNames}
        metricRecordsById={metricRecordsById}
      />
    )
  }

  switch (columnKey) {
    case "status":
      return <StatusIcon status={sim.status} size={20} />
    case "simulationId":
      return (
        <CopyableId id={sim.simulation_output_id} label="Copy Simulation ID" />
      )
    case "callSid":
      return sim.call_sid ? (
        <CopyableId id={sim.call_sid} chars={10} label="Copy CallSid" />
      ) : (
        <>—</>
      )
    case "runId":
      return <CopyableId id={sim.run_id} label="Copy Run ID" />
    case "agentName": {
      const agentId = sim.agent_id
      const agentDisplay = agentId ? agentNames[agentId] || agentId : "-"
      return (
        <ResourceNameCell
          display={agentDisplay}
          bold
          ariaLabel={`Open agent ${agentDisplay}`}
          onClick={
            agentId && onAgentClick ? () => onAgentClick(agentId) : undefined
          }
        />
      )
    }
    case "testSetName": {
      const testSetId = sim.test_set_id
      const display = testSetId ? testSetNames[testSetId] || testSetId : "-"
      return (
        <ResourceNameCell
          display={display}
          ariaLabel={`Open test set ${display}`}
          onClick={
            testSetId && onTestSetClick
              ? () => onTestSetClick(testSetId)
              : undefined
          }
          path={testSetId ? `/test-sets/${testSetId}` : undefined}
        />
      )
    }
    case "personaId": {
      const personaId = sim.persona_id_id
      const display = personaId
        ? personasById[personaId]?.display_name || personaId
        : "-"
      return (
        <ResourceNameCell
          display={display}
          ariaLabel={`Open persona ${display}`}
          onClick={
            personaId && onPersonaClick
              ? () => onPersonaClick(personaId)
              : undefined
          }
          path={personaId ? `/personas/${personaId}` : undefined}
        />
      )
    }
    case "modelType":
      return (
        <TruncatedText className="w-full">
          {getModelType(sim.model_type)}
        </TruncatedText>
      )
    case "tags":
      return (
        <RowEventBoundary>
          <RunTagsCell runId={sim.run_id} initialTags={sim.tags ?? undefined} />
        </RowEventBoundary>
      )
    case "createdBy":
      return (
        <CreatorCell
          display={resolveCreator(sim.created_by_user_id ?? sim.created_by)}
        />
      )
    case "created":
      return <CreatedCell createdAt={sim.created_at} />
    default:
      return null
  }
}
